"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/* "See it on your wall" — real camera preview. Point your phone at the
 * wall, the actual print appears over the live camera feed; drag to
 * position, slide to size, snap a photo. No camera (desktop / denied)?
 * Upload a photo of your wall and place the art on that. This is the
 * wall-art answer to the 3D models' AR — real room, real print. */

const SIZES = ['8×10"', '11×14"', '16×20"', '18×24"', '24×30"'];

type Mode = "idle" | "loading" | "camera" | "upload" | "denied";

export function WallArtRoomPreview({
  imageUrl,
  title,
}: {
  imageUrl: string;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("idle");
  const [bg, setBg] = useState<string | null>(null); // uploaded wall photo
  const [scale, setScale] = useState(0.4); // art width as fraction of stage
  const [pos, setPos] = useState({ x: 50, y: 42 }); // % center
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    setMode("loading");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setMode("camera");
    } catch {
      setMode("denied");
    }
  }, []);

  useEffect(() => {
    if (open) void startCamera();
    return () => stopCamera();
  }, [open, startCamera, stopCamera]);

  /* Drag the art around the stage. Cached rect via ResizeObserver so
   * pointermove doesn't force layout on every pixel. */
  const onArtPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;
    let cachedRect = stage.getBoundingClientRect();
    const ro = new ResizeObserver(() => {
      cachedRect = stage.getBoundingClientRect();
    });
    ro.observe(stage);
    const move = (ev: PointerEvent) => {
      const { left, top, width, height } = cachedRect;
      setPos({
        x: Math.max(8, Math.min(92, ((ev.clientX - left) / width) * 100)),
        y: Math.max(8, Math.min(92, ((ev.clientY - top) / height) * 100)),
      });
    };
    const up = () => {
      ro.disconnect();
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    stopCamera();
    setBg(URL.createObjectURL(file));
    setMode("upload");
  };

  const close = () => {
    stopCamera();
    setBg(null);
    setMode("idle");
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-interactive
        className="group inline-flex items-center gap-2.5 rounded-full border px-6 py-3 font-mono text-[12px] uppercase tracking-[0.12em] text-white transition-all hover:-translate-y-0.5"
        style={{
          borderColor: "rgba(217,163,90,0.4)",
          background: "radial-gradient(120% 100% at 90% -10%, rgba(217,163,90,0.16) 0%, transparent 55%), var(--color-bg-elev-1)",
        }}
      >
        <span className="text-[15px]" aria-hidden>📷</span>
        See it on your wall
      </button>

      {open && (
        <div className="fixed inset-0 z-[150] flex flex-col bg-black">
          {/* Stage */}
          <div ref={stageRef} className="relative flex-1 overflow-hidden">
            {/* Live camera */}
            {(mode === "camera" || mode === "loading") && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-cover" />
            )}
            {/* Uploaded wall photo */}
            {mode === "upload" && bg && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={bg} alt="Your wall" className="absolute inset-0 h-full w-full object-cover" />
            )}
            {/* Loading / denied states */}
            {mode === "loading" && (
              <div className="absolute inset-0 grid place-items-center text-white/70">Starting camera…</div>
            )}
            {mode === "denied" && (
              <div className="absolute inset-0 grid place-items-center p-8 text-center">
                <div>
                  <p className="text-white/80">Camera unavailable. Upload a photo of your wall instead:</p>
                  <label className="mt-5 inline-block cursor-pointer rounded-full px-6 py-3 font-mono text-[12px] uppercase tracking-[0.12em]" style={{ background: "var(--color-accent)", color: "#0b0806" }}>
                    Upload wall photo
                    <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
                  </label>
                </div>
              </div>
            )}

            {/* The art overlay — draggable */}
            {(mode === "camera" || mode === "upload") && (
              <div
                onPointerDown={onArtPointerDown}
                className="absolute cursor-move touch-none select-none"
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  width: `${scale * 100}%`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <div
                  className="rounded-[2px] bg-white p-[5%]"
                  style={{ aspectRatio: "4 / 5", boxShadow: "0 14px 34px -8px rgba(0,0,0,0.6)", border: "0.5vmin solid #2a2a2a" }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={imageUrl} alt={title} draggable={false} className="h-full w-full object-cover" />
                </div>
              </div>
            )}

            {/* Close */}
            <button type="button" onClick={close} aria-label="Close" className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full text-white" style={{ background: "rgba(0,0,0,0.5)" }}>✕</button>

            {/* Hint */}
            {(mode === "camera" || mode === "upload") && (
              <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white/90" style={{ background: "rgba(0,0,0,0.45)" }}>
                Drag the print · slide to resize
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex flex-col gap-3 p-4" style={{ background: "rgba(0,0,0,0.85)" }}>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/65">Size</span>
              <input
                type="range"
                min={0.18}
                max={0.85}
                step={0.01}
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
                className="flex-1 accent-[color:var(--color-accent)]"
                aria-label="Resize the print"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-1.5">
                {SIZES.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScale(0.22 + i * 0.13)}
                    className="rounded-full border px-3 py-1.5 font-mono text-[10.5px] text-white"
                    style={{ borderColor: "rgba(255,255,255,0.25)" }}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <label className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-accent)" }}>
                Use a photo instead
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
              </label>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
